import * as React from "react";
import { useEffect, useState } from "react";
import { vorgangService } from "../../services/vorgangService";
import "../notizbloecke/notizbloecke.css";

/**
 * Zielauswahl für „Block einem Vorgang zuordnen" (PO 08.09.2026).
 *
 * Angeboten werden die Vorgänge DIESES Kunden mit ihren To-dos — das To-do ist
 * die genauere Zuordnung und deshalb direkt wählbar, statt es in einem zweiten
 * Schritt nachzureichen.
 *
 * Bewusst kein Fluent-`<Dialog>`: dessen Surface liegt auf z-index 1000000 und
 * verdeckt die portalisierten Dropdowns dieser Codebasis (100000) — dieselbe
 * Falle, die schon `NotizEditorDialog` umgeht.
 *
 * Ob der Nutzer am Ziel überhaupt schreiben darf, entscheidet das Backend
 * (`PATCH contact-notes/:id/zuordnung`); diese Liste ist Vorauswahl, kein Gate.
 */
export const BlockZuordnenDialog: React.FC<{
    contactId: number;
    onWaehlen: (ziel: { vorgangId: number; todoId: number | null }) => Promise<void>;
    onSchliessen: () => void;
}> = ({ contactId, onWaehlen, onSchliessen }) => {
    const [vorgaenge, setVorgaenge] = useState<
        Array<{ id: number; titel?: string | null; todos?: Array<{ id: number; titel?: string | null }> }>
    >([]);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    useEffect(() => {
        let abgebrochen = false;
        void (async () => {
            try {
                const liste = await vorgangService.getVorgaengeByContact(contactId);
                if (!abgebrochen) setVorgaenge(liste ?? []);
            } catch (err: any) {
                console.error("[BlockZuordnen] Vorgänge laden fehlgeschlagen", err);
                if (!abgebrochen) setFehler("Vorgänge konnten nicht geladen werden.");
            }
        })();
        return () => {
            abgebrochen = true;
        };
    }, [contactId]);

    const waehlen = async (vorgangId: number, todoId: number | null) => {
        if (busy) return;
        setBusy(true);
        setFehler(null);
        try {
            await onWaehlen({ vorgangId, todoId });
            onSchliessen();
        } catch (err: any) {
            // Offen bleiben: ein stilles Schließen sähe aus wie Erfolg, obwohl
            // der Bezug nicht geschrieben wurde (z. B. kein Schreibrecht).
            console.error("[BlockZuordnen] Zuordnen fehlgeschlagen", err);
            setFehler(err?.message ?? "Zuordnen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd nb-zuordnen" role="dialog" aria-label="Block zuordnen">
            <div className="nb-zuordnen-kopf">Einem Vorgang zuordnen</div>
            {fehler ? <div className="nb-zuordnen-fehler">{fehler}</div> : null}
            {vorgaenge.length === 0 ? (
                <div className="nb-zuordnen-leer">Dieser Kunde hat noch keine Vorgänge.</div>
            ) : null}
            {vorgaenge.map((v) => {
                /**
                 * EIN VORGANG MIT TO-DOS IST KEIN ZIEL (PO 22.09.2026) — dort
                 * gehört alles einem To-do. Seine Zeile bleibt stehen, sie ist
                 * die Überschrift über seinen To-dos; nur nimmt sie nichts
                 * mehr an.
                 */
                const hatTodos = (v.todos ?? []).length > 0;
                return (
                    <div key={v.id} className="nb-zuordnen-gruppe">
                        {hatTodos ? (
                            <div className="nb-zuordnen-item nb-zuordnen-kopf">
                                {v.titel || `Vorgang ${v.id}`}
                                <small> — einem To-do zuordnen</small>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="nb-zuordnen-item"
                                disabled={busy}
                                onClick={() => void waehlen(v.id, null)}
                            >
                                {v.titel || `Vorgang ${v.id}`}
                            </button>
                        )}
                        {(v.todos ?? []).map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className="nb-zuordnen-item nb-zuordnen-todo"
                                disabled={busy}
                                onClick={() => void waehlen(v.id, t.id)}
                            >
                                {t.titel || `To-do ${t.id}`}
                            </button>
                        ))}
                    </div>
                );
            })}
        </div>
    );
};
